"use client";

import { useState, useMemo } from "react";
import { glossaryTerms, arabicLetters, getTermsByLetter, getAvailableLetters } from "./glossaryData";
import "./glossary.scss";

export default function GlossaryPage() {
    const [selectedLetter, setSelectedLetter] = useState(null);
    const [searchQuery, setSearchQuery] = useState("");

    const availableLetters = useMemo(() => getAvailableLetters(), []);

    const filteredTerms = useMemo(() => {
        let terms = selectedLetter ? getTermsByLetter(selectedLetter) : glossaryTerms;

        if (searchQuery.trim()) {
            const query = searchQuery.toLowerCase().trim();
            terms = terms.filter(
                (term) =>
                    term.termAr.toLowerCase().includes(query) ||
                    term.termEn.toLowerCase().includes(query) ||
                    term.definitionAr.toLowerCase().includes(query)
            );
        }

        return terms;
    }, [selectedLetter, searchQuery]);

    const handleLetterClick = (letter) => {
        if (selectedLetter === letter) {
            setSelectedLetter(null); // Deselect if clicking the same letter
        } else {
            setSelectedLetter(letter);
        }
        setSearchQuery(""); // Clear search when filtering by letter
    };

    const handleClearAll = () => {
        setSelectedLetter(null);
        setSearchQuery("");
    };

    return (
        <div className="glossary-page">
            <div className="container py-5">
                {/* Header */}
                <div className="glossary-header text-center mb-5">
                    <h1 className="display-4 fw-bold mb-3 text-muted">
                        <span className="text-title text-dark col-md-auto mb-4">القاموس المختصر</span> <span className="d-block col-12 col-md-auto"> لمصطلحات UI/UX <span className="badge fs-6 bg-danger">  إختباري</span></span>
                    </h1>
                    <p className="lead text-muted">
                        دليلك الشامل لفهم مصطلحات تصميم واجهات وتجربة المستخدم باللغة العربية
                    </p>
                </div>

                {/* Search Bar */}
                <div className="search-section mb-4">
                    <div className="search-wrapper">
                        <i className="bi bi-search search-icon"></i>
                        <input
                            type="text"
                            className="form-control search-input"
                            placeholder="ابحث عن مصطلح..."
                            value={searchQuery}
                            onChange={(e) => {
                                setSearchQuery(e.target.value);
                                if (e.target.value.trim()) {
                                    setSelectedLetter(null); // Clear letter filter when searching
                                }
                            }}
                        />
                        {(searchQuery || selectedLetter) && (
                            <button
                                className="btn btn-sm btn-outline-secondary clear-btn"
                                onClick={handleClearAll}
                                title="مسح الفلاتر"
                            >
                                <i className="bi bi-x-circle"></i>
                            </button>
                        )}
                    </div>
                </div>

                {/* Alphabet Filter */}
                <div className="alphabet-filter mb-5">
                    <div className="alphabet-title mb-3">
                        <h3 className="h5 fw-bold">
                            <i className="bi bi-funnel me-2"></i>
                            فلترة حسب الحرف
                        </h3>
                        <span className="filter-badge">
                            {selectedLetter ? `الحرف: ${selectedLetter}` : "كل الحروف"}
                        </span>
                    </div>
                    <div className="alphabet-grid">
                        {arabicLetters.map((letter) => {
                            const isAvailable = availableLetters.includes(letter);
                            const isSelected = selectedLetter === letter;

                            return (
                                <button
                                    key={letter}
                                    className={`alphabet-btn ${isSelected ? "active" : ""} ${!isAvailable ? "disabled" : ""
                                        }`}
                                    onClick={() => isAvailable && handleLetterClick(letter)}
                                    disabled={!isAvailable}
                                    title={isAvailable ? `عرض المصطلحات بحرف ${letter}` : "لا توجد مصطلحات"}
                                >
                                    {letter}
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Results Info */}
                <div className="results-info mb-4">
                    <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                        <div className="results-count">
                            <i className="bi bi-bookmark-check me-2"></i>
                            <span className="fw-bold">{filteredTerms.length}</span> مصطلح
                            {selectedLetter && ` بحرف "${selectedLetter}"`}
                            {searchQuery && ` يطابق "${searchQuery}"`}
                        </div>
                        {(selectedLetter || searchQuery) && (
                            <button className="btn btn-sm btn-outline-dark" onClick={handleClearAll}>
                                <i className="bi bi-arrow-clockwise me-1"></i>
                                عرض جميع المصطلحات
                            </button>
                        )}
                    </div>
                </div>

                {/* Terms Grid */}
                {filteredTerms.length === 0 ? (
                    <div className="no-results text-center py-5">
                        <div className="no-results-icon mb-3">
                            <i className="bi bi-inbox display-1 text-muted"></i>
                        </div>
                        <h3 className="h4 mb-2">لا توجد نتائج</h3>
                        <p className="text-muted mb-3">
                            {searchQuery
                                ? `لم نجد مصطلحات تطابق "${searchQuery}"`
                                : `لا توجد مصطلحات بحرف "${selectedLetter}"`}
                        </p>
                        <button className="btn btn-primary" onClick={handleClearAll}>
                            <i className="bi bi-arrow-clockwise me-2"></i>
                            إعادة تعيين البحث
                        </button>
                    </div>
                ) : (
                    <div className="terms-grid">
                        {filteredTerms.map((term) => (
                            <div key={term.id} className="card term-card">
                                <div className="term-card-header">
                                    <div className="term-letter">{term.letter}</div>
                                    <div className="term-titles">
                                        <h3 className="term-ar">{term.termAr}</h3>
                                        <p className="term-en ltr">{term.termEn}</p>
                                    </div>
                                </div>
                                <div className="term-card-body">
                                    <p className="term-definition">{term.definitionAr}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* Footer Info */}
                <div className="glossary-footer text-center mt-5 pt-4 border-top">
                    <p className="text-muted mb-2">
                        <i className="bi bi-info-circle me-2"></i>
                        هذا القاموس في حالة تطوير مستمر، نسعد بمقترحاتكم لإضافة مصطلحات جديدة
                    </p>
                    <p className="text-muted small">
                        إجمالي المصطلحات المتاحة: <b className="text-dark">{glossaryTerms.length}</b>
                    </p>
                </div>
            </div>
        </div>
    );
}
